import { History, Search, Brain, Fish } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { buildRoute } from '@/shared/lib/constants'

interface AIHistoryEmptyStateProps {
  hasFilters: boolean
  onClearFilters: () => void
}

export function AIHistoryEmptyState({ hasFilters, onClearFilters }: AIHistoryEmptyStateProps) {
  const { farmId } = useParams<{ farmId: string }>()
  const navigate = useNavigate()

  if (hasFilters) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
        <div className="text-center">
          <Search className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-600">
            No analyses match your filters
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Try adjusting your search or filter criteria
          </p>
          <button
            onClick={onClearFilters}
            className="mt-4 px-4 py-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
          >
            Clear all filters
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-80 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
      <div className="text-center max-w-sm">
        <History className="mx-auto h-12 w-12 text-slate-300" />
        <p className="mt-4 text-base font-medium text-slate-600">
          No analysis history yet
        </p>
        <p className="mt-2 text-sm text-slate-400">
          Run your first AI analysis to see results here. Use the Root Cause Analyzer
          to investigate anomalies, or the Fish Health Diagnosis to identify diseases.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={() => farmId && navigate(buildRoute.aiRootCause(farmId))}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200/50 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
          >
            <Brain className="h-4 w-4 text-emerald-600" />
            Root Cause
          </button>
          <button
            onClick={() => farmId && navigate(buildRoute.aiDiagnosis(farmId))}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 rounded-lg text-sm font-medium text-white hover:bg-emerald-700 transition-colors shadow-sm"
          >
            <Fish className="h-4 w-4" />
            Diagnose Fish
          </button>
        </div>
      </div>
    </div>
  )
}

export default AIHistoryEmptyState
